import {
  TabId,
  TabLabelContent,
  TabMain,
  TabOverline,
  TabSeparator,
  TabTitle,
  TabUnseenBadge,
} from 'src/components/GlobalTabs/tab-labels/styled';

const UNSEEN_COUNT_DISPLAY_CAP = 9;

type TabLabelRowsProps = {
  /** Small muted row above the title. Omitted while it is still loading, or in single-partner mode. */
  overline?: string | null;
  /** Friendly id, for entities that have one. Sites and clients do not. */
  id?: string | null;
  /** The identifying text, on its own row at full width. */
  title?: string | null;
  /** Conversations only. Nothing renders at 0; display caps at "9+", the true count stays as-is. */
  unseenCount?: number;
  /** Native tooltip, since either row can truncate. */
  tooltip?: string;
};

/**
 * The two-row tab label, in one place.
 *
 * Every entity's tab label used to compose these rows itself, which meant three copies of the
 * same conditionals — and they drifted: the conversation label gated the separator on
 * multi-tenancy mode while the site and client labels gated it on the partner name actually
 * having arrived. That difference shipped as a visible bug (a delimiter dot floating in front
 * of nothing while the partner loaded). Centralising the composition makes that class of
 * divergence impossible and gives the story something real to render.
 *
 * Each part is dropped rather than rendered empty: an empty reference row still occupies its
 * line height and would push the title off centre, and a separator with nothing before it reads
 * as stray padding.
 */
export const TabLabelRows = ({
  overline,
  id,
  title,
  unseenCount = 0,
  tooltip,
}: TabLabelRowsProps) => {
  const hasReferenceRow = !!overline || !!id;

  return (
    <>
      <TabLabelContent title={tooltip}>
        {hasReferenceRow ? (
          <TabMain>
            {overline ? (
              <>
                <TabOverline>{overline}</TabOverline>
                {id ? <TabSeparator>·</TabSeparator> : null}
              </>
            ) : null}
            {id ? <TabId>{id}</TabId> : null}
          </TabMain>
        ) : null}
        {title ? <TabTitle>{title}</TabTitle> : null}
      </TabLabelContent>
      {unseenCount > 0 ? (
        <TabUnseenBadge>
          {unseenCount > UNSEEN_COUNT_DISPLAY_CAP
            ? `${UNSEEN_COUNT_DISPLAY_CAP}+`
            : unseenCount}
        </TabUnseenBadge>
      ) : null}
    </>
  );
};
